import type { ThemeDto } from "./types";

/**
 * Roboto is self-hosted through `next/font` and exposed as `--font-roboto`.
 * A theme that asks for Roboto should resolve to that optimised face rather
 * than whatever the OS happens to have, so we prepend the variable. Any other
 * font the admin configures is used as-is.
 */
export function fontStack(fontFamily: string): string {
  const wantsRoboto = fontFamily.trim().toLowerCase().startsWith("roboto");
  return wantsRoboto ? `var(--font-roboto), ${fontFamily}` : fontFamily;
}

/**
 * The brand palette only stores three colors. The UI also needs a darker and a
 * lighter step of the primary for gradients and hover states, so we derive them
 * rather than asking admins to pick five colors that have to stay in harmony.
 */
export function brandCssVars(brand: ThemeDto): Record<string, string> {
  return {
    "--brand-primary": brand.primaryColor,
    "--brand-primary-strong": `color-mix(in oklab, ${brand.primaryColor} 72%, #000)`,
    "--brand-primary-soft": `color-mix(in oklab, ${brand.primaryColor} 78%, #fff)`,
    "--brand-secondary": brand.secondaryColor,
    "--brand-accent": brand.accentColor,
    "--brand-font": fontStack(brand.fontFamily),
  };
}

/**
 * Surface tokens derived from the theme's background/surface/text colors.
 *
 * These are only ever applied in light mode: dark mode ships its own tuned
 * palette, and layering an admin's light background over it would destroy
 * contrast. Muted steps are mixed from the theme's own two anchors so a
 * rebrand stays internally consistent instead of drifting toward stock grey.
 */
export function surfaceCssVars(brand: ThemeDto): Record<string, string> {
  const bg = brand.backgroundColor;
  const text = brand.textColor;

  return {
    "--ink": text,
    "--ink-soft": `color-mix(in oklab, ${text} 85%, #808080)`,
    "--bg": bg,
    "--bg-subtle": `color-mix(in oklab, ${bg} 55%, ${brand.surfaceColor})`,
    "--surface": brand.surfaceColor,
    "--surface-muted": `color-mix(in oklab, ${bg} 96%, ${text})`,
    "--surface-raised": brand.surfaceColor,
    "--border": `color-mix(in oklab, ${bg} 90%, ${text})`,
    "--border-strong": `color-mix(in oklab, ${bg} 80%, ${text})`,
    "--text": text,
    "--text-muted": `color-mix(in oklab, ${text} 72%, ${bg})`,
    "--text-subtle": `color-mix(in oklab, ${text} 48%, ${bg})`,
  };
}

/** Every variable a theme controls, flattened for an inline `style` attribute. */
export function themeStyle(brand: ThemeDto): Record<string, string> {
  return { ...brandCssVars(brand), ...surfaceCssVars(brand) };
}

/**
 * The values below are injected into an inline <style> block, so a hostile value
 * could otherwise close that block and open a <script>. Colours are hex-validated
 * server-side, but the font stack is free text, so strip anything that could
 * escape the CSS context before it reaches the document.
 */
function cssSafe(value: string): string {
  return value.replace(/[<>\\{}@;]/g, "");
}

function declarations(vars: Record<string, string>): string {
  return Object.entries(vars)
    .map(([k, v]) => `${k}:${cssSafe(v)}`)
    .join(";");
}

/** Serialises the palette for a first-paint <style> block. */
export function brandCssText(brand: ThemeDto): string {
  return (
    `:root{${declarations(brandCssVars(brand))};}` +
    `:root:not(.dark){${declarations(surfaceCssVars(brand))};}`
  );
}
